import { useState } from "react";
import ConfirmDialog from "../components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Bell, Search, User } from "lucide-react";
import SonomaLogo from "../assets/sonoma-rounded.png";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { DarkModeToggle } from "@/components/theme/DarkModeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { signOut, deleteUser } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { auth, db } from "@/firebase/config";
import { setUser } from "@/State/usersSlice";
import { doc, deleteDoc } from "firebase/firestore";
import { useToast } from "@/components/ui/use-toast";

const OwnerHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    if (confirm("Are you sure you want to permanently delete your account? This cannot be undone.")) {
      const user = auth.currentUser;
      if (user) {
        try {
          setIsDeleting(true);

          // Clear browser storage
          localStorage.clear();
          sessionStorage.clear();

          // Delete Firestore user document
          const userDocRef = doc(db, "users", user.uid);
          await deleteDoc(userDocRef);

          // Delete Firebase Auth user
          await deleteUser(user);

          dispatch(setUser(null));
          toast({
            title: "Account Deleted",
            description: "Your account has been permanently removed.",
            duration: 3000,
          });
          navigate("/owner-login");
        } catch (error) {
          console.error("Account deletion error:", error);
          toast({
            title: "Deletion Failed",
            description: "An error occurred while deleting your account. Please try again.",
            variant: "destructive",
            duration: 5000,
          });
        } finally {
          setIsDeleting(false);
        }
      }
    }
  };

  const handleLogout = async () => {
    setShowLogoutDialog(true);
  };

  const confirmLogout = async () => {
    const user = auth.currentUser;
    if (user) {
      try {
        setIsLoggingOut(true);
        localStorage.clear();
        sessionStorage.clear();
        await signOut(auth);
        dispatch(setUser(null));
        toast({
          title: "Logged Out",
          description: "You have been successfully logged out.",
          duration: 3000,
        });
        navigate("/owner-login");
      } catch (error) {
        console.error("Logout error:", error);
        toast({
          title: "Logout Failed",
          description: "An error occurred during logout. Please try again.",
          variant: "destructive",
          duration: 5000,
        });
      } finally {
        setIsLoggingOut(false);
        setShowLogoutDialog(false);
      }
    } else {
      setShowLogoutDialog(false);
    }
  };

  return (
    <>
      <ConfirmDialog
        open={showLogoutDialog}
        title="Log out?"
        description="Are you sure you want to log out?"
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutDialog(false)}
        confirmText="Log out"
        cancelText="Cancel"
      />
      <header className="w-full bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
        <div className="flex items-center space-x-2 sm:space-x-4">
          <SidebarTrigger className="p-1 sm:p-2" />
          <div className="flex items-center">
            <div className="flex items-center justify-center">
              <img
                src={SonomaLogo}
                alt="Sonoma Logo"
                className="h-[22px] w-[22px] sm:h-[32.5px] sm:w-[32.5px] rounded-md"
                style={{
                  display: 'block',
                  boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
                  WebkitFilter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.10))',
                  filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.18)) contrast(1.2)',
                  background: 'white',
                }}
              />
            </div>
            <div className="flex flex-col leading-tight ml-2 sm:ml-3">
              <span className="text-sm sm:text-lg font-semibold text-gray-800 dark:text-white">
                Sonoma Cloud
              </span>
              <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                Owner Portal
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-1 sm:space-x-3">
          <Button
            variant="ghost"
            size="icon"
            className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            <Search className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            <Bell className="h-4 w-4" />
          </Button>
          <DarkModeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                <User className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
            >
              <DropdownMenuLabel className="text-gray-900 dark:text-white">
                My Account
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-gray-200 dark:bg-gray-700" />
              <DropdownMenuItem className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                Profile Settings
              </DropdownMenuItem>
              <DropdownMenuItem className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                Account Preferences
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-gray-200 dark:bg-gray-700" />
              <DropdownMenuItem
                onClick={handleDeleteAccount}
                className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900"
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete Account"}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900"
                disabled={isLoggingOut}
              >
                {isLoggingOut ? "Logging Out..." : "Logout"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
    </>
  );
};

export default OwnerHeader;